import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { FileText, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface Document {
    id: string;
    filename: string;
    status: string;
    created_at: string;
}

interface QAPair {
    id: string;
    question: string;
    answer: string;
}

const ContributorDashboard: React.FC = () => {
    const { user } = useAuthStore();
    const { t } = useTranslation();
    const [documents, setDocuments] = useState<Document[]>([]);
    const [qaPairs, setQAPairs] = useState<QAPair[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [docsResponse, qaResponse] = await Promise.all([
                    api.get('/users/me/documents'),
                    api.get('/users/me/qa_pairs')
                ]);
                setDocuments(docsResponse.data);
                setQAPairs(qaResponse.data);
            } catch (error) {
                console.error("Failed to fetch contributor data", error);
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-text-primary">{t('home.welcome', 'Welcome, {{name}}!', { name: user?.name || 'User' })}</h1>
                <p className="mt-2 text-text-secondary">{t('contributorDashboard.description', "Here's a summary of your contributions.")}</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('contributorDashboard.myDocuments', 'My Uploaded Documents')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <p>{t('common.loading', 'Loading...')}</p>
                        ) : documents.length > 0 ? (
                            <ul className="space-y-2">
                                {documents.map(doc => (
                                    <li key={doc.id} className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                                            <span>{doc.filename}</span>
                                        </div>
                                        <span className="text-sm text-muted-foreground">{new Date(doc.created_at).toLocaleDateString()}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>{t('contributorDashboard.noDocuments', 'You have not uploaded any documents yet.')}</p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('contributorDashboard.myQAPairs', 'My Validated QA Pairs')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <p>{t('common.loading', 'Loading...')}</p>
                        ) : qaPairs.length > 0 ? (
                            <ul className="space-y-2">
                                {qaPairs.map(qa => (
                                    <li key={qa.id} className="flex items-center">
                                        <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                                        <p className="truncate">{qa.question}</p>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>{t('contributorDashboard.noQAPairs', 'You have not validated any QA pairs yet.')}</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ContributorDashboard;